import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Grid, IconButton, Menu, MenuItem, Alert, Chip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  SportsSoccer,
  EmojiEvents,
  Leaderboard,
  Insights,
  Group,
  Groups,
  CalendarToday,
  TrendingUp,
  Person,
  SmartToy,
  PersonSearch,
  SportsScore,
  CardMembership,
  LocalOffer,
  AdminPanelSettings,
  ToggleOn,
  CalendarMonth,
  Gavel,
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';
import { useTemporadas } from '../hooks/queries';
import useFeatureFlags from '../hooks/useFeatureFlags';
import { StatCard } from '../components/stats';
import { Card, CardHeader, CardContent, Button } from '../design-system/components';
import { Skeleton } from '../design-system/components';
import colors from '../design-system/colors';
import spacing from '../design-system/spacing';
import usePageTitle from '../hooks/usePageTitle';
import { useLicenseTier } from '../hooks/useLicenseTier';
import usePageTour from '../hooks/usePageTour';
import { getUserLicenses } from '../services/license/licenseApiService';
import { useAuth } from '../context/AuthContext';
import { canManagePlanning } from '../utils/planning';
import { getMyTeamManagementSubscriptions } from '../services/teamManagementService';
import WindowsClientBanner from '../components/WindowsClientBanner';
import { DASHBOARD_V2_ENABLED } from '../services/dashboardV2Service';

/** Derive subscription tier from license array + role */
const getSubscriptionTier = (licenses, userRole) => {
  if (userRole === 'admin') return { label: 'Admin', color: colors.primary[700], bg: colors.primary[50] };
  const active = (licenses || []).filter((l) => l.status === 'active');
  const hasPremium = active.some((l) => l.plan === 'pro' || l.plan === 'ultimate');
  const hasBasic = active.some((l) => l.plan === 'basic');
  if (hasPremium) return { label: 'Premium', color: colors.warning[700], bg: colors.warning[50] };
  if (hasBasic) return { label: 'Basic', color: colors.info[700], bg: colors.info[50] };
  return { label: 'Free', color: colors.neutral[600], bg: colors.neutral[100] };
};

/**
 * Quick Action Card
 */
const QuickActionCard = ({ icon: Icon, title, description, onClick, color, tourId }) => {
  const theme = useTheme();

  return (
  <Card
    data-tour={tourId}
    onClick={onClick}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    role="button"
    tabIndex={0}
    sx={{
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backgroundColor: 'background.paper',
      border: `1px solid ${theme.palette.divider}`,
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 6,
        borderColor: alpha(color || theme.palette.primary.main, 0.8),
      },
    }}
    component={motion.div}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: spacing.borderRadius.md,
            backgroundColor: color || colors.primary[500],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ color: colors.neutral.white, fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom color="text.primary">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
  );
};

const DashboardModern = () => {
  usePageTitle('Dashboard');
  usePageTour('dashboard');
  const { t } = useTranslation('dashboard');
  const theme = useTheme();
  const navigate = useNavigate();
  const { isFeatureEnabled } = useFeatureFlags();
  // eslint-disable-next-line no-unused-vars
  const { filters } = useAppContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const [canManageTeams, setCanManageTeams] = useState(false);
  
  // Obtener datos con React Query
  const { data: temporadas, isLoading: loadingTemporadas, isError: errorTemporadas, refetch: refetchTemporadas } = useTemporadas();

  const { user: usuario } = useAuth();
  const nombreUsuario = usuario?.nombre || 'Usuario';
  const rolUsuario = usuario?.rol || 'usuario';
  const userId = usuario?.id || null;

  // Tier de suscripción con hook compartido
  const subscriptionTier = useLicenseTier(userId, rolUsuario);

  useEffect(() => {
    if (rolUsuario === 'admin') {
      setCanManageTeams(false);
      return undefined;
    }

    let active = true;
    getMyTeamManagementSubscriptions()
      .then((subscriptions) => {
        if (!active) return;
        setCanManageTeams(subscriptions.length > 0);
      })
      .catch(() => {
        if (active) setCanManageTeams(false);
      });
    return () => {
      active = false;
    };
  }, [rolUsuario]);

  // Quick actions - páginas principales
  const quickActions = [
    {
      icon: EmojiEvents,
      title: t('quickActions.standings.title'),
      description: t('quickActions.standings.description'),
      onClick: () => navigate('/clasificacion'),
      color: colors.primary[500],
    },
    {
      icon: SportsSoccer,
      title: t('quickActions.matches.title'),
      description: t('quickActions.matches.description'),
      onClick: () => navigate('/partidos'),
      color: colors.football.win,
    },
    {
      icon: Leaderboard,
      title: t('quickActions.rankingPlayers.title'),
      description: t('quickActions.rankingPlayers.description'),
      onClick: () => navigate('/rankingJugadores'),
      color: colors.secondary[600],
      tourId: 'dashboard-ranking-card',
    },
    {
      icon: Insights,
      title: t('quickActions.statsPlayers.title'),
      description: t('quickActions.statsPlayers.description'),
      onClick: () => navigate('/estadisticas'),
      color: colors.primary[700],
      tourId: 'dashboard-stats-card',
    },
    {
      icon: Insights,
      title: t('quickActions.statsTeams.title'),
      description: t('quickActions.statsTeams.description'),
      onClick: () => navigate('/estadisticasEquipos'),
      color: colors.football.draw,
    },
    {
      icon: Gavel,
      title: t('quickActions.statsReferees.title'),
      description: t('quickActions.statsReferees.description'),
      onClick: () => navigate('/estadisticasArbitros'),
      color: colors.warning[700],
    },
    {
      icon: Group,
      title: t('quickActions.clubs.title'),
      description: t('quickActions.clubs.description'),
      onClick: () => navigate('/clubes'),
      color: colors.primary[600],
    },
    {
      icon: Person,
      title: t('quickActions.individual.title'),
      description: t('quickActions.individual.description'),
      onClick: () => navigate('/analisisIndividual'),
      color: colors.primary[400],
    },
    {
      icon: SmartToy,
      title: t('quickActions.aiAgent.title'),
      description: t('quickActions.aiAgent.description'),
      onClick: () => navigate('/agente'),
      color: colors.info.main,
    },
    {
      icon: PersonSearch,
      title: t('quickActions.scouting.title'),
      description: t('quickActions.scouting.description'),
      onClick: () => navigate('/scouting'),
      color: colors.secondary[500],
      tourId: 'dashboard-scouting-card',
    },
    ...(isFeatureEnabled('planificacion') && canManagePlanning(usuario)
      ? [{
          icon: CalendarMonth,
          title: t('quickActions.planning.title'),
          description: t('quickActions.planning.description'),
          onClick: () => navigate('/planificacion'),
          color: colors.primary[600],
        }]
      : []),
    {
      icon: SportsScore,
      title: t('quickActions.liveTag.title'),
      description: t('quickActions.liveTag.description'),
      onClick: () => navigate('/live-tag'),
      color: colors.football.win,
      tourId: 'dashboard-live-tag-card',
    },
    {
      icon: CardMembership,
      title: t('quickActions.licenses.title'),
      description: t('quickActions.licenses.description'),
      onClick: () => navigate('/licencias'),
      color: colors.neutral[600],
    },
    ...(canManageTeams
      ? [{
          icon: Groups,
          title: 'Administrar equipos',
          description: 'Asigna equipos federativos según los límites de tu plan.',
          onClick: () => navigate('/administrar-equipos'),
          color: colors.secondary[600],
        }]
      : []),
  ];

  // Filter quick actions based on feature flags
  const filteredQuickActions = quickActions.filter((action) => {
    if (action.onClick.toString().includes('/agente')) {
      return isFeatureEnabled('chatIA');
    }
    if (action.onClick.toString().includes('/licencias')) {
      return isFeatureEnabled('licencias');
    }
    return true;
  });

  // Stats de ejemplo (estos vendrían de APIs en producción)
  const stats = [
    {
      title: t('stats.seasons'),
      value: temporadas?.length || 0,
      icon: CalendarToday,
      iconColor: colors.primary[500],
      suffix: '',
    },
    {
      title: t('stats.active'),
      value: temporadas?.[temporadas.length - 1]?.nombre || '-',
      icon: TrendingUp,
      iconColor: colors.football.win,
      suffix: '',
    },
  ];

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: 'background.default',
        backgroundImage: theme.palette.mode === 'dark'
          ? 'radial-gradient(circle at 10% 0%, rgba(76, 175, 80, 0.12), transparent 30%), radial-gradient(circle at 90% 8%, rgba(255, 213, 79, 0.08), transparent 24%)'
          : 'none',
        color: 'text.primary',
        minHeight: '100vh',
      }}
    >
      {/* Header */}
      <Box
        data-tour="dashboard-header"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 0.5,
            }}
          >
            {t('greeting', { name: nombreUsuario })}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body1" color="text.secondary">
              {rolUsuario === 'admin' ? t('subtitle.admin') : t('subtitle.user')}
            </Typography>
            {subscriptionTier && (
              <Chip
                label={t('plan', { tier: subscriptionTier.label })}
                size="small"
                sx={{
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(subscriptionTier.color, 0.24)
                    : subscriptionTier.bg,
                  color: theme.palette.mode === 'dark'
                    ? theme.palette.text.primary
                    : subscriptionTier.color,
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  height: 22,
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/licencias')}
              />
            )}
          </Box>
        </Box>
        {DASHBOARD_V2_ENABLED && (
          <Button variant="contained" onClick={() => navigate('/dashboard-v2')} sx={{ flexShrink: 0, ml: 2 }}>
            {t('dashboardV2:newVersion')}
          </Button>
        )}
      </Box>

      {/* Stats Cards */}
      {errorTemporadas && (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button variant="text" color="inherit" size="small" onClick={() => refetchTemporadas()}>{t('errors.retry')}</Button>}>
          {t('errors.loadData')}
        </Alert>
      )}
      {loadingTemporadas ? (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton type="card" />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }} data-tour="dashboard-stats">
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <StatCard {...stat} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Quick Actions */}
      {/* Windows client banner — prominently before secondary content */}
      <Box sx={{ mb: 4 }} data-testid="dashboard-windows-banner">
        <WindowsClientBanner />
      </Box>

      {/* Quick Actions */}
      <Box sx={{ mb: 4 }} data-tour="dashboard-quick-actions">
        <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: 'text.primary' }}>
          {t('quickActions.sectionTitle')}
        </Typography>
        <Grid container spacing={3}>
          {filteredQuickActions.map((action, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <QuickActionCard {...action} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Admin section */}
      {rolUsuario === 'admin' && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: 'text.primary' }}>
            {t('admin.sectionTitle')}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <QuickActionCard
                icon={AdminPanelSettings}
                title={t('admin.analytics.title')}
                description={t('admin.analytics.description')}
                onClick={() => navigate('/admin/analytics')}
                color={colors.secondary[700]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <QuickActionCard
                icon={CardMembership}
                title={t('admin.licenses.title')}
                description={t('admin.licenses.description')}
                onClick={() => navigate('/admin/licencias')}
                color={colors.secondary[600]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <QuickActionCard
                icon={LocalOffer}
                title={t('admin.promoCodes.title')}
                description={t('admin.promoCodes.description')}
                onClick={() => navigate('/admin/promo-codes')}
                color={colors.secondary[500]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <QuickActionCard
                icon={ToggleOn}
                title={t('admin.featureFlags.title')}
                description={t('admin.featureFlags.description')}
                onClick={() => navigate('/admin/feature-flags')}
                color={colors.secondary[400]}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Info adicional */}
      <Card data-tour="dashboard-tips">
        <CardHeader title={t('tips.title')} />
        <CardContent>
          <Typography variant="body2" color={'text.secondary'} paragraph>
            {t('tips.tip1')}
          </Typography>
          <Typography variant="body2" color={'text.secondary'} paragraph>
            {t('tips.tip2')}
          </Typography>
          <Typography variant="body2" color={'text.secondary'}>
            {t('tips.tip3')}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardModern;
